import { Drawer as BaseDrawer } from "@base-ui/react/drawer";
import { useMediaQuery } from "@base-ui/react/unstable-use-media-query";
import { cva, type VariantProps } from "class-variance-authority";
import { createContext, use } from "react";
import { cn } from "@/lib/utils";
import { usePortalHost } from "../../providers/portal-host";
import { Icon, type IconName } from "../icon";
import { IconButton } from "../icon-button";

type DrawerSize = "default" | "small";
type DrawerSide = "left" | "right";

type DrawerContextValue = {
  responsive: boolean;
  side: DrawerSide;
  isBottomSheet: boolean;
};

const DrawerContext = createContext<DrawerContextValue>({
  responsive: true,
  side: "right",
  isBottomSheet: false,
});

const backdropStyles = cva([
  "fixed",
  "inset-0",
  "z-[var(--z-layer-dialog)]",
  "bg-surface-scrim",
  "transition-opacity",
  "duration-200",
  "data-ending-style:opacity-0",
  "data-starting-style:opacity-0",
]);

const viewportStyles = cva(
  [
    "fixed",
    "inset-0",
    "z-[var(--z-layer-dialog)]",
    "flex",
    "pointer-events-none",
  ],
  {
    variants: {
      side: {
        left: "justify-start",
        right: "justify-end",
      },
      isBottomSheet: {
        false: null,
        true: "items-end justify-center",
      },
      responsive: {
        false: null,
        true: "max-[904px]:items-end max-[904px]:justify-center",
      },
    },
  },
);

const popupVariants = cva(
  [
    "pointer-events-auto",
    "flex",
    "h-dvh",
    "max-h-dvh",
    "min-w-0",
    "flex-col",
    "overflow-hidden",
    "border",
    "border-outline-subtle",
    "bg-surface",
    "text-text-default",
    "shadow-elevation-4",
    "outline-none",
    "transition-transform",
    "duration-200",
    "ease-out",
  ],
  {
    variants: {
      size: {
        default: ["[--drawer-size:640px]", "w-[min(100vw,var(--drawer-size))]"],
        small: ["[--drawer-size:560px]", "w-[min(100vw,var(--drawer-size))]"],
      },
      side: {
        left: [
          "data-ending-style:-translate-x-full",
          "data-starting-style:-translate-x-full",
        ],
        right: [
          "data-ending-style:translate-x-full",
          "data-starting-style:translate-x-full",
        ],
      },
      contentVariant: {
        default: null,
        navigation: "border-0",
      },
      isBottomSheet: {
        false: null,
        true: [
          "h-auto",
          "min-h-[230px]",
          "max-h-[min(680px,calc(100dvh_-_var(--spacing-16)))]",
          "w-full",
          "rounded-t-(--radius-sm)",
          "data-ending-style:translate-x-0",
          "data-ending-style:translate-y-full",
          "data-starting-style:translate-x-0",
          "data-starting-style:translate-y-full",
        ],
      },
      responsive: {
        false: null,
        true: [
          "max-[904px]:h-auto",
          "max-[904px]:min-h-[230px]",
          "max-[904px]:max-h-[min(680px,calc(100dvh_-_var(--spacing-16)))]",
          "max-[904px]:w-full",
          "max-[904px]:rounded-t-(--radius-sm)",
          "max-[904px]:data-ending-style:translate-x-0",
          "max-[904px]:data-ending-style:translate-y-full",
          "max-[904px]:data-starting-style:translate-x-0",
          "max-[904px]:data-starting-style:translate-y-full",
        ],
      },
    },
    defaultVariants: {
      size: "default",
    },
  },
);

const contentStyles = cva(
  ["flex", "min-h-0", "flex-1", "flex-col", "overflow-hidden", "bg-surface"],
  {
    variants: {
      variant: {
        default: ["gap-(--spacing-16)", "pt-(--spacing-10)"],
        navigation: ["gap-0", "pt-0"],
      },
    },
    defaultVariants: { variant: "default" },
  },
);

const headerStyles = cva([
  "flex",
  "shrink-0",
  "items-start",
  "justify-between",
  "gap-(--spacing-2)",
  "bg-surface",
  "mx-(--spacing-10)",
  "p-(--spacing-8)",
]);

const headerMainStyles = cva([
  "flex",
  "min-w-0",
  "flex-1",
  "items-start",
  "gap-(--spacing-8)",
]);

const headerTitleGroupStyles = cva([
  "flex",
  "min-w-0",
  "flex-1",
  "flex-col",
  "gap-(--spacing-4)",
]);

const titleStyles = cva([
  "font-(family-name:--font-family-title)",
  "text-(length:--font-size-title-lg)",
  "font-(number:--font-weight-medium)",
  "leading-(--font-line-height-28)",
  "text-text-default",
]);

const subtitleStyles = cva([
  "text-(length:--font-size-body-medium)",
  "leading-(--font-line-height-20)",
  "text-text-subtext",
]);

const bodyStyles = cva([
  "min-h-0",
  "flex-1",
  "overflow-y-auto",
  "px-(--spacing-20)",
  "max-[904px]:pb-[env(safe-area-inset-bottom)]",
]);

const bodyInnerVariants = cva(["flex", "flex-col"], {
  variants: {
    gap: {
      none: "gap-0",
      md: "gap-(--spacing-20)",
    },
  },
  defaultVariants: {
    gap: "md",
  },
});

const footerStyles = cva([
  "flex",
  "min-h-(--spacing-80)",
  "shrink-0",
  "items-center",
  "justify-end",
  "gap-(--spacing-10)",
  "border-t",
  "border-outline-subtle",
  "bg-surface",
  "px-(--spacing-20)",
  "py-(--spacing-16)",
  "max-[904px]:pb-[calc(var(--spacing-16)_+_env(safe-area-inset-bottom))]",
]);

export interface DrawerProps
  extends Omit<
    React.ComponentProps<typeof BaseDrawer.Root>,
    "children" | "modal" | "swipeDirection"
  > {
  children: React.ReactNode;
  /** Side from which the drawer enters on desktop. */
  side?: DrawerSide;
  /** Whether the drawer becomes a bottom sheet at the responsive breakpoint. */
  responsive?: boolean;
}

export interface DrawerTriggerProps
  extends React.ComponentProps<typeof BaseDrawer.Trigger> {}

export interface DrawerCloseProps
  extends React.ComponentProps<typeof BaseDrawer.Close> {}

export interface DrawerContentProps
  extends Omit<React.ComponentProps<typeof BaseDrawer.Popup>, "className">,
    VariantProps<typeof popupVariants> {
  children: React.ReactNode;
  /** Target drawer width. Defaults to "default" (640px). */
  size?: DrawerSize;
  /** Internal popup content layout. */
  contentVariant?: "default" | "navigation";
}

export interface DrawerHeaderProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "className" | "title"> {
  /** Drawer heading. Used as the dialog accessible name. */
  title: React.ReactNode;
  /** Optional supporting text rendered as the dialog accessible description. */
  subtitle?: React.ReactNode;
  /** Optional leading icon, such as the AI sparkle treatment in Figma. */
  leadingIcon?: IconName;
  /** Optional status chip or summary accessory rendered beside the title. */
  accessory?: React.ReactNode;
  /** Optional header-level action, such as a CTA button. */
  actions?: React.ReactNode;
  /** Accessible label for the close button. */
  closeLabel?: string;
}

export interface DrawerBodyProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "className">,
    VariantProps<typeof bodyInnerVariants> {
  children: React.ReactNode;
}

export interface DrawerFooterProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "className"> {
  children: React.ReactNode;
}

function Drawer({ children, ...props }: DrawerProps) {
  const { side = "right", responsive = true, ...rootProps } = props;
  const matchesMobileBreakpoint = useMediaQuery("(max-width: 904px)", {
    defaultMatches: false,
  });
  const isBottomSheet = responsive && matchesMobileBreakpoint;

  return (
    <DrawerContext value={{ responsive, side, isBottomSheet }}>
      <BaseDrawer.Root
        modal
        swipeDirection={
          isBottomSheet ? "down" : side === "left" ? "left" : "right"
        }
        {...rootProps}
      >
        {children}
      </BaseDrawer.Root>
    </DrawerContext>
  );
}

function DrawerTrigger(props: DrawerTriggerProps) {
  return <BaseDrawer.Trigger {...props} />;
}

function DrawerClose(props: DrawerCloseProps) {
  return <BaseDrawer.Close {...props} />;
}

function DrawerContent({
  children,
  size,
  contentVariant = "default",
  initialFocus = true,
  ...props
}: DrawerContentProps) {
  const portalHost = usePortalHost();
  const { responsive, side, isBottomSheet } = use(DrawerContext);

  return (
    <BaseDrawer.Portal container={portalHost}>
      <BaseDrawer.Backdrop className={cn(backdropStyles())} />
      <BaseDrawer.Viewport
        className={cn(viewportStyles({ responsive, side, isBottomSheet }))}
      >
        <BaseDrawer.Popup
          aria-modal="true"
          initialFocus={initialFocus}
          className={cn(
            popupVariants({
              responsive,
              size,
              side,
              isBottomSheet,
              contentVariant,
            }),
          )}
          {...props}
        >
          <BaseDrawer.Content
            className={cn(contentStyles({ variant: contentVariant }))}
          >
            {children}
          </BaseDrawer.Content>
        </BaseDrawer.Popup>
      </BaseDrawer.Viewport>
    </BaseDrawer.Portal>
  );
}

function DrawerHeader({
  title,
  subtitle,
  leadingIcon,
  accessory,
  actions,
  closeLabel = "Close drawer",
  ...props
}: DrawerHeaderProps) {
  return (
    <div className={cn(headerStyles())} {...props}>
      <div className={cn(headerMainStyles())}>
        {leadingIcon && (
          <span className="mt-(--spacing-4) inline-flex shrink-0 text-icon-main">
            <Icon name={leadingIcon} size="sm" tone="inherit" />
          </span>
        )}
        <div className={cn(headerTitleGroupStyles())}>
          <div className="flex min-w-0 flex-wrap items-center gap-(--spacing-8)">
            <BaseDrawer.Title className={cn(titleStyles())}>
              {title}
            </BaseDrawer.Title>
            {accessory}
          </div>
          {subtitle && (
            <BaseDrawer.Description className={cn(subtitleStyles())}>
              {subtitle}
            </BaseDrawer.Description>
          )}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-(--spacing-8)">
        {actions}
        <BaseDrawer.Close
          render={
            <IconButton
              icon="x-close"
              aria-label={closeLabel}
              variant="ghost"
              size="md"
            />
          }
        />
      </div>
    </div>
  );
}

function DrawerBody({ children, gap, ...props }: DrawerBodyProps) {
  return (
    <div className={cn(bodyStyles())} {...props}>
      <div className={cn(bodyInnerVariants({ gap }))}>{children}</div>
    </div>
  );
}

function DrawerFooter({ children, ...props }: DrawerFooterProps) {
  return (
    <div className={cn(footerStyles())} {...props}>
      {children}
    </div>
  );
}

export {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTrigger,
};
export type { DrawerSide, DrawerSize };
